import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHeading, StepList, FAQAccordion, CTABand } from "@/components/PageBlocks";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const GROUPS = ["Milestone birthdays", "Family reunions", "Friends' getaways", "Destination celebrations", "Destination weddings", "Group cruises", "Resort groups", "Corporate retreats", "Incentive travel", "Clubs & organizations", "Multi-generational travel"];

const COORDINATE = ["Destination & experience selection", "Accommodations", "Cruise or resort options", "Room & cabin coordination", "Transportation", "Activities & excursions", "Dining considerations", "Group deadlines", "Traveler communications", "Trip information", "Individual traveler questions"];

const STEPS = [
  { title: "Tell Us About Your Group", desc: "Share the occasion, approximate group size, travel window, interests, and any ideas you already have." },
  { title: "We Design the Experience", desc: "We help narrow the options and build a travel plan around your group's priorities." },
  { title: "We Help Coordinate the Details", desc: "Once plans are confirmed, we help keep trip information, timelines, and traveler needs organized." },
  { title: "Your Group Travels", desc: "Spend less time managing logistics and more time enjoying why everyone came together." },
];

const FAQ = [
  { q: "How many travelers make a group?", a: "Group sizes vary widely. Whether it's a handful of close friends or a large celebration, we can help — just tell us roughly how many people you expect." },
  { q: "How early should we start planning?", a: "Earlier is easier, especially for popular dates, cruises, and larger groups. Reaching out several months ahead gives you the most options, though we're happy to help with shorter timelines too." },
  { q: "Can travelers come from different cities?", a: "Yes. Groups often travel from multiple locations, and we can help account for that when planning the trip." },
  { q: "Can Inforno help with cruises and resorts?", a: "Yes. Cruises and all-inclusive resorts are among the most popular choices for groups, and we can help you compare options." },
  { q: "Can individual travelers make their own payments?", a: "Depending on the trip setup, individual travelers may be able to handle their own arrangements. We'll walk you through what's possible for your specific trip." },
  { q: "Can you help with destination celebrations or weddings?", a: "Yes. Milestone celebrations, reunions, and destination weddings are a natural fit for group travel planning." },
  { q: "What information do you need to get started?", a: "Just the basics to begin — the occasion, approximate group size, a travel window, and what you're hoping the trip feels like." },
];

export default function GroupTravel() {
  useSeo({ title: "Group Travel Made Easy | Inforno Travel", description: "Planning a trip for a group? Inforno Travel helps organize accommodations, transportation, activities, and details so you can enjoy the experience." });
  return (
    <div className="bg-noir min-h-screen">
      <Navbar />
      <PageHero overline="Group Travel" title="Group Travel, Made Easy." image={IMAGES.cruise}
        sub="The best group trips bring people together. The planning behind them can pull you in a hundred directions. Inforno Travel helps organize the details so you can spend less time coordinating and more time looking forward to the experience."
        primary={{ label: "Plan Your Group Journey", to: "/start-your-journey" }}
        secondary={{ label: "Explore Experiences", to: "/experiences" }} />

      {/* A. Why */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <SectionHeading overline="Why Group Travel With Inforno" title={<>One trip. A lot of moving pieces.</>} />
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.1}>
              <p className="font-body text-white/60 text-sm md:text-base leading-relaxed font-light">
                Coordinating multiple travelers can involve accommodations, transportation, payments, deadlines,
                activities, dining, communications, and a lot of individual questions. We help bring those pieces
                together — depending on your trip, we can help with as much or as little as you need.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* B. Groups We Help Plan */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-12"><SectionHeading overline="Groups We Help Plan" title="Every kind of gathering" /></div>
          <div className="flex flex-wrap gap-3">
            {GROUPS.map((g, i) => (
              <Reveal key={g} delay={(i % 6) * 0.04}>
                <span className="inline-block border border-white/15 text-white/75 px-5 py-3 font-body text-sm hover:border-gold hover:text-gold transition-colors">{g}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* C. What We Can Help Coordinate */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-12"><SectionHeading overline="What We Can Help Coordinate" title="Depending on your trip, we can help with…" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {COORDINATE.map((c, i) => (
              <Reveal key={c} delay={(i % 3) * 0.05}>
                <div className="flex items-center gap-4 py-3 border-b border-white/10 text-white/75 font-body text-sm">
                  <Check size={15} className="text-gold shrink-0" /> {c}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* D. How It Works */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-14"><SectionHeading overline="How It Works" title="A simple, four-step path" /></div>
          <StepList steps={STEPS} />
        </div>
      </section>

      {/* F. FAQ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
        <div className="max-w-[900px] mx-auto">
          <div className="mb-10"><SectionHeading overline="Group FAQ" title="Good questions to start with" /></div>
          <FAQAccordion items={FAQ} />
        </div>
      </section>

      <CTABand eyebrow="Let's get started" title="Plan a trip your whole group will remember." label="Plan Your Group Journey" to="/start-your-journey" showRtb />
      <Footer />
    </div>
  );
}
